import React, { useState } from "react"
import { useIntl } from "react-intl"
import { collapseSideBar } from "context/applicationContext"
import * as paths from "constants/paths"
import SubHeader from "components/Shared/SubHeader"
import { filtersToShow } from "components/CustomerBehavior/LoyaltyPerformance/Loyalty/Shared/utils"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import ChartContainer from "components/Shared/ChartContainer"
import {
	Alert,
	Chip,
	ChipGroup,
	Divider,
} from "@walmart-web/livingdesign-components"
import {
	TreeCustomer,
	foreignObjectProps,
	nodeSize,
	translateConf,
	separation,
	GROWTH_NODE,
} from "./Shared/chartUtils"
import Tree from "react-d3-tree"
import GenerateNode from "components/Shared/GenerateNode"
import {
	CustomerSegmentationResponse,
	GrowthProfilingResponse,
} from "./Shared/interfaces"
import { RawNodeDatum } from "react-d3-tree/lib/types/common"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import { getGrowthProfilingDescription, getGrowthTree } from "./service"
import {
	customersSelectGrowthProfiling,
	noDataError,
	validateValuePercentage,
} from "./utils"
import CustomerSegmentation from "./CustomerSegmentation"
import Location from "components/Shared/Filters/modules/Location"
import Product from "components/Shared/Filters/modules/Product"
import Segmentation from "components/Shared/Filters/modules/Segmentation"
import Channel from "components/Shared/Filters/modules/Channel"
import Format from "components/Shared/Filters/modules/Format"
import {
	BRANDS,
	COUNTIES,
	FORMATS,
	LOCATION_GROUP_FILTER,
	PRODUCTS,
	PRODUCT_GROUP_FILTER,
	AGE,
	GENDER,
	CHANNEL,
	CHANNEL_GROUP_FILTER,
	REGIONS,
	STORES,
	FINELINE,
	FORMAT_GROUP_FILTER,
	SEGMENTATION_GROUP_FILTER,
	PSYCHOGRAPHICS,
} from "components/Shared/GlobalFilter/constants"
import Filters from "components/Shared/Filters"
import Selectors, {
	DEFAULT_PROPS,
} from "components/Shared/Filters/modules/Vendor"
import DisplayError from "components/Shared/DisplayError"
import NoResultsImage from "assets/images/error/search_page_error.svg"
import { concatClassNames } from "helpers/jsxHelpers"
import PhronesisCalendar from "components/Shared/PhronesisCalendar/PhronesisCalendar"
import { DateInterval } from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import "./index.scss"
import { hasValidFilters } from "components/Shared/utils/utils"
import { isFeatureEnabled } from "../../../config/feature-flags"

const panelContent: Record<string, JSX.Element> = {
	[LOCATION_GROUP_FILTER]: <Location />,
	[PRODUCT_GROUP_FILTER]: <Product />,
	[FORMAT_GROUP_FILTER]: <Format />,
	[SEGMENTATION_GROUP_FILTER]: <Segmentation />,
	[CHANNEL_GROUP_FILTER]: <Channel />,
}

const filtersProperties = {
	...DEFAULT_PROPS,
	[FINELINE]: {
		allOption: true,
		isMulti: true,
	},
}

const GrowthProfiling: React.FC = () => {
	collapseSideBar()

	const intl = useIntl()
	const { filters } = useFiltersContext()
	const [growthTreeCustomer, setGrowthTreeCustomer] = useState<string>(
		TreeCustomer.identCustomers
	)
	const [dateInterval, setDateInterval] = useState<DateInterval>({
		currentFrom: "",
		currentTo: "",
		previousFrom: "",
		previousTo: "",
	})

	const heightTree =
		customersSelectGrowthProfiling[growthTreeCustomer] === "IDENTIFIED"
			? "heightIdentifiedCustomer"
			: "heightAllCustomer"
	const validateSalesInfo = () =>
		isFetching || isFetchingDescription || !!data.attributes?.amount
	const isDataFetching = () => isFetching || isFetchingDescription

	const menuButtons = [
		{
			title: intl.formatMessage({ id: "phronesis.filters.brandandproduct" }),
			toOpen: PRODUCT_GROUP_FILTER,
			dependencies: [BRANDS, PRODUCTS],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.channel" }),
			toOpen: CHANNEL_GROUP_FILTER,
			dependencies: [CHANNEL],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.format" }),
			toOpen: FORMAT_GROUP_FILTER,
			dependencies: [FORMATS],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.groups.location" }),
			toOpen: LOCATION_GROUP_FILTER,
			dependencies: [REGIONS, COUNTIES, STORES],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.segmentation" }),
			toOpen: SEGMENTATION_GROUP_FILTER,
			dependencies: [AGE, GENDER, PSYCHOGRAPHICS],
		},
	]

	const RenderChipCustomer = (props: any) => (
		<Chip
			data-testid="chip"
			selected={growthTreeCustomer === props.selection}
			size="large"
			onClick={() => {
				setGrowthTreeCustomer(props.selection)
			}}
		>
			{intl.formatMessage({
				id: props.message,
			})}
		</Chip>
	)

	const {
		data = {} as GrowthProfilingResponse,
		isFetching,
		isError: isGrowthProfilingResponseError,
		error: growthProfilingResponseError,
		refetch: growthProfilingRefetch,
	} = useFetchData<GrowthProfilingResponse>(
		"retrieveGrowthTree",
		getGrowthTree,
		{
			filters,
			dateInterval,
			customers: customersSelectGrowthProfiling[growthTreeCustomer],
		},
		{
			enabled: hasValidFilters(dateInterval),
			retry: false,
		}
	)

	const {
		data: descriptionGrowthProfiling = {} as CustomerSegmentationResponse,
		isFetching: isFetchingDescription,
		isError: isCustomerSegmentationError,
		error: customerSegmentationError,
		refetch: customerSegmentationRefetch,
	} = useFetchData<CustomerSegmentationResponse>(
		"retrieveGrowthDescription",
		getGrowthProfilingDescription,
		{
			filters,
			dateInterval,
			customers: customersSelectGrowthProfiling[growthTreeCustomer],
		},
		{
			retry: false,
			enabled:
				customersSelectGrowthProfiling[growthTreeCustomer] === "IDENTIFIED",
		}
	)

	const ShowCustomerSegmentation = () => {
		return (
			growthTreeCustomer === TreeCustomer.identCustomers &&
			isFeatureEnabled("customerSegmentation")
		)
	}

	const RenderGrowthTree = () => {
		return (
			<div
				className={concatClassNames(["container-growth-tree " + heightTree])}
			>
				{!isFetching && (
					<Tree
						translate={translateConf}
						data={data as RawNodeDatum}
						zoomable={false}
						zoom={0.85}
						orientation="vertical"
						nodeSize={nodeSize}
						pathClassFunc={() => "custom-link"}
						pathFunc="step"
						collapsible={false}
						separation={separation}
						renderCustomNodeElement={(treeProps) =>
							GenerateNode(
								{ ...treeProps, foreignObjectProps },
								GROWTH_NODE,
								intl
							)
						}
					/>
				)}
			</div>
		)
	}

	const GrowthTreeOptions = () => (
		<ChipGroup className="decision-tree-buttons">
			<RenderChipCustomer
				selection={TreeCustomer.allCustomers}
				message="phronesis.transactional.application.growth-profiling.chart.allcustomer.button"
			/>
			<RenderChipCustomer
				selection={TreeCustomer.identCustomers}
				message="phronesis.transactional.application.growth-profiling.chart.idencustomer.button"
			/>
		</ChipGroup>
	)

	const RenderGrowthTables = () => (
		<div className="growth-charts">
			<ChartContainer
				title={{
					id:
						"phronesis.transactional.application.growth-profiling.chart.title",
				}}
				isLoading={isDataFetching()}
				buttons={GrowthTreeOptions()}
			>
				<Alert UNSAFE_className="message-description" variant="info">
					{intl.formatMessage(
						{
							id:
								customersSelectGrowthProfiling[growthTreeCustomer] ===
								"IDENTIFIED"
									? validateValuePercentage(
											descriptionGrowthProfiling.customerInformation
												?.percentageSalesByCustomers,
											"phronesis.transactional.application.growth-profiling.chart.description.idenCustomer",
											"phronesis.transactional.application.growth-profiling.chart.description.idenCustomer.lessThanOne"
									  )
									: "phronesis.transactional.application.growth-profiling.chart.description.allCustomer",
						},
						{
							totalCustomers: intl.formatNumber(
								descriptionGrowthProfiling.customerInformation?.totalCustomers,
								{
									style: "decimal",
								}
							),
							percentageSalesByCustomers: intl.formatNumber(
								descriptionGrowthProfiling.customerInformation
									?.percentageSalesByCustomers,
								{
									style: "percent",
									minimumFractionDigits: 1,
								}
							),
							br: <br />,
							b: (str: any) => <b>{str}</b>,
						}
					)}
				</Alert>
				<RenderGrowthTree />
				{ShowCustomerSegmentation() && (
					<React.Fragment>
						<Divider className="content-divider" />
						<CustomerSegmentation
							descriptionCustomerSegmentation={descriptionGrowthProfiling}
						/>
					</React.Fragment>
				)}
			</ChartContainer>
		</div>
	)

	const getErrorServices = () => {
		const isError =
			isCustomerSegmentationError || isGrowthProfilingResponseError
		const error = customerSegmentationError || growthProfilingResponseError

		return { isError, error }
	}

	const refetchServices = () => {
		growthProfilingRefetch()
		customerSegmentationRefetch()
	}

	const { isError, error } = getErrorServices()

	return (
		<div className="growth-profiling">
			<SubHeader
				title={"phronesis.transactional"}
				subTitle={"phronesis.transactional.application.growth-profiling.title"}
				titleContent={
					<div className="phronesis-calendar">
						<PhronesisCalendar
							setDateInterval={(interval) => {
								setDateInterval(interval)
							}}
						/>
					</div>
				}
				parentPath={paths.TRANSACTIONAL}
				filters={filtersToShow}
				content={
					<Filters dataMenu={{ panelContent, buttons: menuButtons }}>
						<Selectors selectProps={filtersProperties} />
					</Filters>
				}
			/>
			<AsyncMessageResponseHandler
				hasData
				isLoading={false}
				error={error}
				isError={isError}
				retry={refetchServices}
			>
				{validateSalesInfo() ? (
					<RenderGrowthTables />
				) : (
					renderMessage(noDataError, NoResultsImage)
				)}
			</AsyncMessageResponseHandler>
		</div>
	)
}

const renderMessage = (
	errorDescription: Error,
	imgError?: string,
	hasRetry = false
) => {
	return (
		<DisplayError error={errorDescription} img={imgError} hasRetry={hasRetry} />
	)
}

export default GrowthProfiling
